import React, { useState } from 'react';
import { ArrowLeft, Check, AlertTriangle } from 'lucide-react';
import { Question } from '../types';
import { evaluateDynamicVerdict } from '../utils/dynamicVerdictEngine';

interface QuizViewProps {
  questions: Question[];
  onComplete: (answers: Record<number, string>) => void;
  onBackToLanding: () => void;
}

export const QuizView: React.FC<QuizViewProps> = ({
  questions,
  onComplete,
  onBackToLanding,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const currentQuestion = questions[currentIndex];
  const totalQuestions = questions.length;
  const progressPercent = Math.round(((currentIndex + 1) / totalQuestions) * 100);

  // 严格根据 35 题四段式节奏计算“异常失控等级”
  // 0: Q01-Q10 (现实基准), 1: Q11-Q20 (性格与微异), 2: Q21-Q28 (荒诞偏离), 3: Q29-Q35 (彻底失控)
  const anomalyLevel = currentIndex < 10 ? 0 : currentIndex < 20 ? 1 : currentIndex < 28 ? 2 : 3;

  // 动态研究所实时观察注记
  const dynamicVerdict = evaluateDynamicVerdict(answers, currentIndex);

  const handleSelectOption = (optionId: string) => {
    if (isTransitioning) return;

    setSelectedOptionId(optionId);
    setIsTransitioning(true);

    const updatedAnswers = {
      ...answers,
      [currentQuestion.id]: optionId,
    };
    setAnswers(updatedAnswers);

    // 190ms 触感延迟后平滑进入下一题
    setTimeout(() => {
      if (currentIndex < totalQuestions - 1) {
        setCurrentIndex((prev) => prev + 1);
        setSelectedOptionId(null);
        setIsTransitioning(false);
      } else {
        onComplete(updatedAnswers);
      }
    }, 190);
  };

  const handlePrev = () => {
    if (isTransitioning) return;
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
      setSelectedOptionId(answers[questions[currentIndex - 1].id] || null);
    } else {
      onBackToLanding();
    }
  };

  // 反应烈度元数据解析
  const getIntensityMeta = (intensity: 1 | 2 | 3 | 4 = 1, stage: 1 | 2 | 3 | 4 = 1) => {
    switch (intensity) {
      case 1:
        return {
          dots: '●○○○',
          roman: 'Ⅰ',
          text: stage === 4 ? '隐忍' : stage === 3 ? '常态' : '平稳',
          colorClass: 'text-ink-400',
          selectedColorClass: 'text-paper-300',
        };
      case 2:
        return {
          dots: '●●○○',
          roman: 'Ⅱ',
          text: stage === 4 ? '动摇' : stage === 3 ? '妥协' : '适度',
          colorClass: 'text-ink-500',
          selectedColorClass: 'text-paper-200',
        };
      case 3:
        return {
          dots: '●●●○',
          roman: 'Ⅲ',
          text: stage === 4 ? '解构' : stage === 3 ? '剧烈' : '显著',
          colorClass: stage >= 3 ? 'text-hazard-dark font-bold' : 'text-ink-600 font-medium',
          selectedColorClass: 'text-hazard-light font-bold',
        };
      case 4:
        return {
          dots: '●●●●',
          roman: 'Ⅳ',
          text: stage === 4 ? '熔断' : stage === 3 ? '临界' : '极限',
          colorClass: stage === 4 ? 'text-stamp font-black' : stage === 3 ? 'text-stamp font-bold' : 'text-ink-800 font-bold',
          selectedColorClass: 'text-stamp-light font-black',
        };
    }
  };

  return (
    <div className={`min-h-[100dvh] flex flex-col px-5 py-5 max-w-md mx-auto relative overflow-hidden transition-colors duration-500 ${
      anomalyLevel === 3
        ? 'bg-[#F2ECE4]'
        : anomalyLevel === 2
        ? 'bg-[#F5EFE6]'
        : 'bg-paper-50'
    }`}>
      {/* 顶部控制栏 */}
      <header className="relative z-10 space-y-2.5">
        <div className="flex items-center justify-between border-b border-ink-300 pb-2">
          <button
            onClick={handlePrev}
            type="button"
            className="inline-flex items-center gap-1 font-mono text-xs font-semibold text-ink-700 hover:text-ink-950 py-1 px-1.5 -ml-1 rounded-xs hover:bg-paper-200 transition-colors cursor-pointer"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            <span>{currentIndex === 0 ? '返回' : '上一题'}</span>
          </button>

          {/* 进度显示：严格 01 / 35 格式 */}
          <div className="flex items-center gap-2">
            {anomalyLevel >= 2 && (
              <span className="text-[10px] font-mono text-stamp bg-stamp-light border border-stamp/40 px-1 py-0.2 animate-pulse">
                [ANOMALY-ALERT]
              </span>
            )}
            <div className="font-mono text-xs font-black tracking-widest text-ink-900 bg-paper-200 px-2 py-0.5 border border-ink-400">
              <span className="text-ink-950 text-base">
                {String(currentIndex + 1).padStart(2, '0')}
              </span>
              <span className="text-ink-400 mx-1">/</span>
              <span className="text-ink-500">{String(totalQuestions).padStart(2, '0')}</span>
            </div>
          </div>
        </div>

        {/* 严谨的刻度进度条 */}
        <div className="space-y-1">
          <div className="w-full h-2 bg-paper-200 border border-ink-400 p-[1px]">
            <div
              className={`h-full transition-all duration-200 ${
                anomalyLevel === 3
                  ? 'bg-stamp'
                  : anomalyLevel === 2
                  ? 'bg-hazard-dark'
                  : 'bg-ink-900'
              }`}
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <div className="flex justify-between text-[10px] font-mono text-ink-400">
            <span>START</span>
            <span>
              {anomalyLevel === 0 && 'STAGE 1: 现实物理基准 (Q01-10)'}
              {anomalyLevel === 1 && 'STAGE 2: 性格微异渗入 (Q11-20)'}
              {anomalyLevel === 2 && 'STAGE 3: 荒诞偏离加速 (Q21-28)'}
              {anomalyLevel === 3 && 'STAGE 4: 现实防线失控 (Q29-35)'}
            </span>
            <span>END</span>
          </div>
        </div>
      </header>

      {/* 题目内容区：紧接进度区向下排列，长内容自然纵向滚动 */}
      <main className="relative z-10 mt-4 mb-6 space-y-4 animate-slide-up" key={currentQuestion.id}>
        {/* 分类标签与系统注记 */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="inline-block px-2 py-0.5 text-[10px] font-mono font-bold uppercase tracking-wider bg-ink-900 text-paper-50">
              {currentQuestion.category}
            </span>
            <span className="text-[10px] font-mono text-ink-400">
              PARAM_ID: #{currentQuestion.id * 31}
            </span>
          </div>

          {/* 动态研究所实时观察注记 */}
          <div
            className={`p-2 text-xs font-mono border transition-all duration-300 flex items-center justify-between gap-2 shadow-sharp-xs ${
              dynamicVerdict.level === 'critical'
                ? 'bg-stamp text-paper-50 border-stamp font-black animate-pulse'
                : dynamicVerdict.level === 'hazard'
                ? 'bg-stamp-light/85 border-stamp text-stamp-dark font-bold'
                : dynamicVerdict.level === 'notice'
                ? 'bg-hazard-light/60 border-hazard-dark/60 text-hazard-dark font-medium'
                : 'bg-paper-200/80 border-ink-400/80 text-ink-800'
            }`}
          >
            <div className="flex items-center gap-1.5 min-w-0">
              <span
                className={`w-1.5 h-1.5 shrink-0 rounded-full ${
                  dynamicVerdict.level === 'critical'
                    ? 'bg-paper-50 animate-ping'
                    : dynamicVerdict.level === 'hazard'
                    ? 'bg-stamp'
                    : dynamicVerdict.level === 'notice'
                    ? 'bg-hazard-dark'
                    : 'bg-ink-600'
                }`}
              />
              <span className="leading-tight truncate">{dynamicVerdict.text}</span>
            </div>
            <span className="shrink-0 text-[9px] font-bold opacity-75 tracking-wider font-mono">
              [{dynamicVerdict.badgeText}]
            </span>
          </div>

          <h2 className="text-xl font-black text-ink-950 leading-snug tracking-tight pt-1">
            {currentQuestion.title}
          </h2>
        </div>

        {/* 选项列表 */}
        <div className="space-y-2 pt-1">
          {currentQuestion.options.map((option) => {
            const isSelected = selectedOptionId === option.id;
            const meta = option.intensity ? getIntensityMeta(option.intensity, currentQuestion.stage) : null;
            return (
              <button
                key={option.id}
                onClick={() => handleSelectOption(option.id)}
                type="button"
                className={`w-full text-left p-3.5 border-2 transition-all duration-150 cursor-pointer select-none relative ${
                  isSelected
                    ? 'bg-ink-950 text-paper-50 border-ink-950 shadow-sharp-sm translate-x-0.5 translate-y-0.5'
                    : 'bg-white/95 text-ink-900 border-ink-800 hover:bg-paper-100 hover:border-ink-950 shadow-sharp-sm active:translate-x-0.5 active:translate-y-0.5'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div
                    className={`w-6 h-6 border flex items-center justify-center font-mono text-xs font-black shrink-0 mt-0.5 transition-colors ${
                      isSelected
                        ? 'bg-paper-50 text-ink-950 border-paper-50'
                        : 'bg-paper-100 text-ink-800 border-ink-800'
                    }`}
                  >
                    {isSelected ? <Check className="w-3.5 h-3.5 stroke-[3]" /> : option.id}
                  </div>

                  <div className="flex-1 min-w-0">
                    {meta && (
                      <div className="flex items-center gap-1.5 mb-1 text-[10px] font-mono tracking-wider select-none">
                        <span className={isSelected ? meta.selectedColorClass : meta.colorClass}>
                          {meta.dots} {meta.roman} · {meta.text}
                        </span>
                      </div>
                    )}
                    <p className={`text-[15px] font-bold leading-snug ${
                      isSelected ? 'text-paper-50' : 'text-ink-950'
                    }`}>
                      {option.label}
                    </p>
                    {option.desc && (
                      <p className={`text-[13px] mt-1 font-mono leading-tight ${
                        isSelected ? 'text-paper-300' : 'text-ink-500'
                      }`}>
                        {option.desc}
                      </p>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </main>

      {/* 底部状态提示：吸底 */}
      <footer className="relative z-10 mt-auto py-2 border-t border-ink-200 flex items-center justify-between text-[10px] font-mono text-ink-400">
        <span>单选直达下一题</span>
        <span>系统状态: {anomalyLevel === 3 ? '严重失控偏离' : '样本评定中'}</span>
      </footer>
    </div>
  );
};
